import { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router';
import { Box, Stack, Typography, Alert, useMediaQuery } from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { TimeEntry } from '@altyor-kronos/shared-types';
import { useAuth } from '../../../core/auth/useAuth';
import {
  createCalendarTimeEntry,
  updateCalendarTimeEntry,
  updateTimeEntryTiming,
  deleteTimeEntry,
} from '../../../core/data/kronosMutations';
import { getWeekRange } from '../timesheet';
import { buildPastePayload, type ClipboardEntry } from './calendarClipboard';
import {
  useTrackerProjects,
  useProjectDesignations,
  useAllUsers,
  useMyRecentEntries,
  useMultiProjectDesignations,
} from '../useKronosData';
import { useCalendarEntries } from './useCalendarEntries';
import {
  buildDesignationOptions,
  buildRecencyMap,
  buildRecentPairOptions,
  decodeDesignationOptionId,
} from '../designationOptions';
import { buildTaskSuggestions } from '../taskSuggestions';
import { colors } from '../../../core/theme/theme';
import { useResponsive } from '../../../hooks/useResponsive';
import { useOrientation } from '../../../hooks/useOrientation';
import CalendarToolbar, { type CalendarViewMode } from './CalendarToolbar';
import CalendarGrid from './CalendarGrid';
import CalendarEntryPopover from './CalendarEntryPopover';
import type { DragResult } from './calendarDrag';
import { useTimeLock } from '../useTimeLock';
import TeammateScopePicker from '../TeammateScopePicker';
import {
  canViewOtherUsersEntries,
  canEditOtherUsersEntries,
} from '../../../core/auth/entryScopeAccess';
import { formatMonthKey } from '../CloseMonthDialog';

export default function CalendarView() {
  const { t, i18n } = useTranslation();
  const { isMonthClosed, lockedThrough } = useTimeLock();
  const { user } = useAuth();
  const { isMobile } = useResponsive();
  const orientation = useOrientation();
  const isLandscape = orientation === 'landscape';
  // Any viewport that cannot comfortably host a hero + toolbar + headers + a
  // full day of cells must give the grid the full page scroll instead of the
  // nested one (landscape phones and short tablet windows fall here).
  const isShortViewport = useMediaQuery('(max-height: 750px)');
  const fullPageScroll = isMobile || isShortViewport;
  // Teammate selector lives below the hero (centered) on mobile portrait;
  // elsewhere it sits in the toolbar where the "Today" button would be.
  const teammateBelowHero = isMobile && !isLandscape;
  const [searchParams, setSearchParams] = useSearchParams();

  // ─── View mode & date from query params ─────────────────────
  const viewMode = (searchParams.get('view') as CalendarViewMode) || 'week';
  const dateParam = searchParams.get('date');
  const anchor = dateParam ? new Date(dateParam) : new Date();
  if (isNaN(anchor.getTime())) anchor.setTime(Date.now());

  const weekRange = getWeekRange(anchor);

  const setViewMode = (mode: CalendarViewMode) => {
    const p = new URLSearchParams(searchParams);
    p.set('view', mode);
    setSearchParams(p, { replace: true });
  };

  const setAnchorDate = (d: Date) => {
    const p = new URLSearchParams(searchParams);
    const iso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    p.set('date', iso);
    setSearchParams(p, { replace: true });
  };

  // ─── Navigation ─────────────────────────────────────────────
  const handlePrev = () => {
    const offset = viewMode === 'week' ? 7 : 1;
    const d = new Date(anchor);
    d.setDate(d.getDate() - offset);
    setAnchorDate(d);
  };

  const handleNext = () => {
    const offset = viewMode === 'week' ? 7 : 1;
    const d = new Date(anchor);
    d.setDate(d.getDate() + offset);
    setAnchorDate(d);
  };

  const handleToday = () => {
    const p = new URLSearchParams(searchParams);
    p.delete('date');
    setSearchParams(p, { replace: true });
  };

  // ─── Teammate selector ──────────────────────────────────────
  // Le jeton Entra ne porte jamais `rd_manager` ni `manager` : s'y fier
  // affichait le sélecteur à des gens dont la requête 403 (CLAUDE.md § Roles).
  const isManager = canViewOtherUsersEntries(user?.roles);
  const canEditOthers = canEditOtherUsersEntries(user?.roles);
  const allUsers = useAllUsers(isManager);
  // Pickers list only active teammates; the selected-label lookup (below) still
  // uses the full list so an already-selected user who goes inactive keeps their name.
  const activeTeammates = allUsers.users.filter((u) => u.data.active !== false);

  const [selectedTeammateId, setSelectedTeammateId] = useState('');
  const targetUserId = selectedTeammateId || user?.uid || '';
  const isViewingTeammate = Boolean(selectedTeammateId && selectedTeammateId !== user?.uid);
  // Regarder les saisies d'un collègue n'est plus synonyme de lecture seule :
  // un admin corrige, et le serveur estampille sa correction à son nom.
  const isReadOnlyScope = isViewingTeammate && !canEditOthers;
  const isEditingForOther = isViewingTeammate && canEditOthers;

  // ─── Data fetching ──────────────────────────────────────────
  const days = useMemo(() => {
    if (viewMode === 'day') {
      return [new Date(anchor.getFullYear(), anchor.getMonth(), anchor.getDate())];
    }
    // Week: 7 days starting from weekRange.start (Monday)
    return Array.from({ length: 7 }, (_, i) => {
      const d = new Date(weekRange.start);
      d.setDate(d.getDate() + i);
      return d;
    });
  }, [viewMode, anchor.getTime(), weekRange.start.getTime()]);

  const rangeStart = days[0];
  const rangeEnd = new Date(days[days.length - 1]);
  rangeEnd.setHours(23, 59, 59, 999);

  const calEntries = useCalendarEntries(targetUserId, rangeStart, rangeEnd);

  const assignedProjects = useTrackerProjects(user ?? null);

  // Popover state for project-dependent designations
  const [popoverProjectId, setPopoverProjectId] = useState('');
  const projDesignations = useProjectDesignations(popoverProjectId);

  // Build label maps
  const projectNames = useMemo(() => {
    const m = new Map<string, string>();
    for (const p of assignedProjects.projects) {
      m.set(p.id, `${p.data.code} — ${p.data.name}`);
    }
    return m;
  }, [assignedProjects.projects]);

  const projectColors = useMemo(() => {
    // Project type has no `color` field yet — map is empty/undefined
    // for all entries, so getProjectColor falls back to the hash.
    return new Map<string, string | undefined>();
  }, []);

  const designationNames = useMemo(() => {
    const m = new Map<string, string>();
    for (const d of projDesignations.designations) {
      m.set(d.id, d.data.name);
    }
    return m;
  }, [projDesignations.designations]);

  // Build popover options
  const popoverProjects = useMemo(
    () =>
      assignedProjects.projects.map((p) => ({
        id: p.id,
        code: p.data.code,
        name: p.data.name,
      })),
    [assignedProjects.projects],
  );

  // ─── Recent-use history (feeds the popover's suggestions) ───
  // A fixed 30-day trailing window of the user's own entries, deliberately
  // independent of the visible range: opening an empty week must still
  // suggest what you actually work on.
  const recentSince = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    d.setDate(d.getDate() - 30);
    return d;
  }, []);
  const myRecent = useMyRecentEntries(user?.uid ?? '', recentSince);

  // Raw designationId → last used (ms), scoped to the popover's project.
  // Floats those designations to the top of the picker and badges them
  // "récent" — same contract as the tracker form.
  const recentDesignationIds = useMemo(
    () => buildRecencyMap(myRecent.entries.map((e) => e.data), popoverProjectId),
    [myRecent.entries, popoverProjectId],
  );

  // Designation labels for every project touched in the window — the
  // cross-project list spans projects the popover isn't subscribed to.
  const recentProjectIds = useMemo(
    () => Array.from(new Set(myRecent.entries.map((e) => e.data.projectId))),
    [myRecent.entries],
  );
  const recentDesignationLabels = useMultiProjectDesignations(recentProjectIds, {
    excludeArchived: true,
  });

  const projectCodes = useMemo(() => {
    const m = new Map<string, string>();
    for (const p of assignedProjects.projects) m.set(p.id, p.data.code);
    return m;
  }, [assignedProjects.projects]);

  // Shown in the designation picker *before* a project is chosen: the
  // last (project, designation) pairs logged. Picking one fills both.
  const recentPairOptions = useMemo(
    () =>
      buildRecentPairOptions(
        myRecent.entries.map((e) => e.data),
        projectCodes,
        recentDesignationLabels,
        t('kronos.calendar.recentGroup'),
      ),
    [myRecent.entries, projectCodes, recentDesignationLabels, t],
  );

  const popoverDesignations = useMemo(
    () =>
      buildDesignationOptions(
        projDesignations.designations.map((d) => d.data),
        [],
        {
          sectionLabel: (s) => s,
          existingGroupLabel: t('kronos.tracker.designationsOnProject'),
          recentGroupLabel: t('kronos.tracker.designationsRecent'),
        },
        recentDesignationIds,
      ),
    [projDesignations.designations, t, recentDesignationIds],
  );

  // Description suggestions for the popover. With a project selected the
  // list is scoped to it; with none, `buildTaskSuggestions` switches to
  // global mode and returns the last tasks across every project — and
  // uses `designationLabelsById` as its validity check, so that map has
  // to span the whole window, not just the popover's project.
  const validPopoverDesignationIds = useMemo(
    () => new Set(projDesignations.designations.map((d) => d.id)),
    [projDesignations.designations],
  );
  const allDesignationLabels = useMemo(() => {
    const m = new Map(recentDesignationLabels);
    for (const [id, name] of designationNames) m.set(id, name);
    return m;
  }, [recentDesignationLabels, designationNames]);
  const taskSuggestions = useMemo(
    () =>
      buildTaskSuggestions(
        myRecent.entries.map((e) => e.data),
        popoverProjectId,
        {
          validDesignationIds: validPopoverDesignationIds,
          designationLabelsById: allDesignationLabels,
          projectLabelsById: projectNames,
        },
      ),
    [
      myRecent.entries,
      popoverProjectId,
      validPopoverDesignationIds,
      allDesignationLabels,
      projectNames,
    ],
  );

  // ─── Date label ─────────────────────────────────────────────
  const dateLabel = useMemo(() => {
    if (viewMode === 'day') {
      return anchor.toLocaleDateString(undefined, {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      });
    }
    const start = weekRange.start;
    const end = new Date(weekRange.start);
    end.setDate(end.getDate() + 6);
    const fmt = (d: Date) =>
      d.toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
    return `${fmt(start)} – ${fmt(end)} ${end.getFullYear()}`;
  }, [viewMode, anchor.getTime(), weekRange.start.getTime()]);

  // ─── Popover state ──────────────────────────────────────────
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [popoverAnchorPos, setPopoverAnchorPos] = useState<{ top: number; left: number } | undefined>(undefined);
  const [popoverStart, setPopoverStart] = useState(0);
  const [popoverEnd, setPopoverEnd] = useState(0);
  const [popoverDate, setPopoverDate] = useState<Date>(new Date());
  const [popoverEntry, setPopoverEntry] = useState<{ id: string; data: TimeEntry } | null>(null);

  // ─── Selection & clipboard (copy/paste) ─────────────────────
  const [selectedEntryId, setSelectedEntryId] = useState<string | null>(null);
  // App-level clipboard: holds the copied entry's data until overwritten.
  // A ref (not state) because nothing needs to re-render when it changes.
  const clipboardRef = useRef<ClipboardEntry | null>(null);
  // Last hovered grid slot, tracked so Ctrl+V can paste under the cursor
  // (keydown events carry no pointer position).
  const hoverPosRef = useRef<{ dayIndex: number; minute: number } | null>(null);

  const openPopover = (
    x: number,
    y: number,
    date: Date,
    startMin: number,
    endMin: number,
    entry?: { id: string; data: TimeEntry },
  ) => {
    setPopoverAnchorPos({ top: y, left: x });
    setPopoverDate(date);
    setPopoverStart(startMin);
    setPopoverEnd(endMin);
    setPopoverEntry(entry ?? null);
    setPopoverOpen(true);
  };

  const closePopover = () => {
    setPopoverOpen(false);
    setPopoverAnchorPos(undefined);
    setPopoverEntry(null);
  };

  // ─── Handlers ───────────────────────────────────────────────
  // Single click selects (highlights) the entry without opening the editor.
  const handleSelectEntry = useCallback((entryId: string) => {
    setSelectedEntryId(entryId);
  }, []);

  // Double click opens the edit popover.
  const handleEntryEdit = (entryId: string, x: number, y: number) => {
    const entry = calEntries.entries.find((e) => e.id === entryId);
    if (!entry) return;
    const st = entry.data.startTime.toDate();
    const et = entry.data.endTime.toDate();
    openPopover(
      x, y,
      st,
      st.getHours() * 60 + st.getMinutes(),
      et.getHours() * 60 + et.getMinutes(),
      entry,
    );
  };

  const handleEmptySlotClick = (dayIndex: number, minute: number, x: number, y: number) => {
    if (isReadOnlyScope) return;
    if (isMonthClosed(days[dayIndex])) return;
    setSelectedEntryId(null);
    const date = days[dayIndex];
    openPopover(x, y, date, minute, minute + 30);
  };

  // Create a copy of the clipboard entry at the target slot (immediate write).
  const pasteAt = useCallback(
    async (clip: ClipboardEntry, target: { date: Date; startMinute: number }) => {
      const p = buildPastePayload(clip, target);
      const startDate = new Date(p.date);
      startDate.setHours(0, p.startMinute, 0, 0);
      const endDate = new Date(p.date);
      endDate.setHours(0, p.endMinute, 0, 0);
      await createCalendarTimeEntry({
        userId: targetUserId,
        projectId: p.projectId,
        designationId: p.designationId,
        description: p.description,
        startTime: startDate,
        endTime: endDate,
        durationSeconds: (p.endMinute - p.startMinute) * 60,
        tags: p.tags,
        billable: p.billable,
      });
    },
    [targetUserId],
  );

  const handleDragMove = async (entryId: string, result: DragResult, anchorDate: Date) => {
    const entry = calEntries.entries.find((e) => e.id === entryId);
    if (!entry) return;

    const newDate = days[result.day] ?? anchorDate;
    const newStart = new Date(newDate);
    newStart.setHours(0, result.startMinute, 0, 0);
    const newEnd = new Date(newDate);
    newEnd.setHours(0, result.endMinute, 0, 0);
    const duration = (result.endMinute - result.startMinute) * 60;

    await updateTimeEntryTiming(entryId, {
      startTime: newStart,
      endTime: newEnd,
      durationSeconds: duration,
    });
  };

  const handleDragResize = async (entryId: string, result: DragResult) => {
    const entry = calEntries.entries.find((e) => e.id === entryId);
    if (!entry) return;

    const existingDate = entry.data.startTime.toDate();
    const newStart = new Date(existingDate);
    newStart.setHours(0, result.startMinute, 0, 0);
    const newEnd = new Date(existingDate);
    newEnd.setHours(0, result.endMinute, 0, 0);
    const duration = (result.endMinute - result.startMinute) * 60;

    await updateTimeEntryTiming(entryId, {
      startTime: newStart,
      endTime: newEnd,
      durationSeconds: duration,
    });
  };

  const handleSave = async (payload: {
    projectId: string;
    designationId: string;
    description: string;
    startMinute: number;
    endMinute: number;
    date: Date;
    tags: string[];
    billable: boolean;
  }) => {
    const startDate = new Date(payload.date);
    startDate.setHours(0, payload.startMinute, 0, 0);
    const endDate = new Date(payload.date);
    endDate.setHours(0, payload.endMinute, 0, 0);
    const durationSeconds = (payload.endMinute - payload.startMinute) * 60;

    // Decode the encoded option id (e.g. "existing:abc123") to the raw Firestore doc id.
    const decoded = decodeDesignationOptionId(payload.designationId);
    const rawDesignationId = decoded?.kind === 'existing' ? decoded.id : payload.designationId;

    if (popoverEntry) {
      // Update
      await updateCalendarTimeEntry(popoverEntry.id, {
        projectId: payload.projectId,
        designationId: rawDesignationId,
        description: payload.description,
        startTime: startDate,
        endTime: endDate,
        durationSeconds,
        tags: payload.tags,
        billable: payload.billable,
      });
    } else {
      // Create
      await createCalendarTimeEntry({
        userId: targetUserId,
        projectId: payload.projectId,
        designationId: rawDesignationId,
        description: payload.description,
        startTime: startDate,
        endTime: endDate,
        durationSeconds,
        tags: payload.tags,
        billable: payload.billable,
      });
    }
  };

  const handleDelete = async (entryId: string) => {
    await deleteTimeEntry(entryId);
  };

  // ─── Keyboard copy/paste & deselect ─────────────────────────
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setSelectedEntryId(null);
        return;
      }
      if (!(e.ctrlKey || e.metaKey)) return;

      // Don't hijack native copy/paste while typing in the popover form.
      const target = e.target as HTMLElement | null;
      if (
        target &&
        (target.tagName === 'INPUT' ||
          target.tagName === 'TEXTAREA' ||
          target.isContentEditable)
      ) {
        return;
      }

      const key = e.key.toLowerCase();
      if (key === 'c') {
        if (isReadOnlyScope || !selectedEntryId) return;
        const entry = calEntries.entries.find((en) => en.id === selectedEntryId);
        if (!entry) return;
        const st = entry.data.startTime.toDate();
        const et = entry.data.endTime.toDate();
        clipboardRef.current = {
          projectId: entry.data.projectId,
          designationId: entry.data.designationId,
          description: entry.data.description ?? '',
          tags: entry.data.tags ?? [],
          billable: entry.data.billable,
          durationMinutes: Math.round((et.getTime() - st.getTime()) / 60000),
        };
      } else if (key === 'v') {
        if (isReadOnlyScope) return;
        const clip = clipboardRef.current;
        const hover = hoverPosRef.current;
        if (!clip || !hover) return;
        const date = days[hover.dayIndex];
        if (!date) return;
        // Coller VERS un mois clôturé : le seul chemin d'écriture qui
        // n'entre pas par la colonne, donc le seul à garder son propre garde.
        if (isMonthClosed(date)) return;
        e.preventDefault();
        void pasteAt(clip, { date, startMinute: hover.minute });
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isReadOnlyScope, selectedEntryId, calEntries.entries, days, pasteAt, isMonthClosed]);

  // ─── Error state ────────────────────────────────────────────
  if (calEntries.error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{calEntries.error.message}</Alert>
      </Box>
    );
  }

  const teammateDisplayName =
    allUsers.users.find((u) => u.id === selectedTeammateId)?.data.displayName ?? '';

  const readOnlyTooltip = t('kronos.calendar.readOnly', { name: teammateDisplayName });
  const weekHasClosedDay = days.some((d) => isMonthClosed(d));

  // AppShell chrome: TopBar 64px + content padding (40px top + 40px bottom at md).
  // We use a negative margin to reclaim the padding and fill the viewport.
  // On mobile the whole calendar scrolls with the page (no nested scroll), so the
  // outer container takes its natural height instead of pinning to the viewport.
  return (
    <Box
      data-testid="calendar-view"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: fullPageScroll ? 'auto' : 'calc(100vh - 64px)',
        mx: { xs: -2, sm: -3, md: -5 },
        mb: { xs: -2, sm: -3, md: -5 },
        mt: { xs: -2, sm: -3, md: -5 },
      }}
    >
      {/* Compact hero header with background image */}
      <Box
        sx={{
          position: 'relative',
          overflow: 'hidden',
          minHeight: 140,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'flex-end',
          px: { xs: 2, sm: 3 },
          py: { xs: 1.5, sm: 2 },
        }}
      >
        <Box
          component="img"
          src="/images/calendar-header.webp"
          alt=""
          sx={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            objectPosition: 'center 30%',
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            bgcolor: 'rgba(20, 23, 28, 0.55)',
          }}
        />
        <Stack spacing={0.5} sx={{ position: 'relative', zIndex: 1 }}>
          <Typography variant="h2" sx={{ color: colors.white, fontWeight: 700 }}>
            {t('kronos.calendar.title')}
          </Typography>
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }}>
            {t('kronos.calendar.subtitle')}
          </Typography>
        </Stack>
      </Box>

      {/* Dedicated row for the teammate selector on mobile portrait
          (managers only). Sits below the hero and is centered. In landscape
          or on larger screens the selector lives in the toolbar instead. */}
      {teammateBelowHero && isManager && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            px: 2,
            pt: 1,
          }}
        >
          <TeammateScopePicker
            users={allUsers.users}
            value={selectedTeammateId}
            onChange={setSelectedTeammateId}
            placeholder={t('kronos.calendar.teammate')}
          />
        </Box>
      )}

      <CalendarToolbar
        viewMode={viewMode}
        onViewModeChange={setViewMode}
        dateLabel={dateLabel}
        onPrev={handlePrev}
        onNext={handleNext}
        onToday={handleToday}
        showTeammateSelector={isManager && !teammateBelowHero}
        showTodayButton={!isMobile}
        teammates={activeTeammates}
        selectedTeammateId={selectedTeammateId}
        onTeammateChange={setSelectedTeammateId}
      />

      {isEditingForOther ? (
        <Alert severity="warning" sx={{ mx: { xs: 2, sm: 3 }, mb: 1 }}>
          {t('kronos.onBehalf.banner', { name: teammateDisplayName })}
        </Alert>
      ) : null}

      {weekHasClosedDay && lockedThrough ? (
        <Alert severity="info" sx={{ mx: { xs: 2, sm: 3 }, mb: 1 }}>
          {t('kronos.timeLock.banner', {
            month: formatMonthKey(lockedThrough, i18n.language),
          })}
        </Alert>
      ) : null}

      <CalendarGrid
        days={days}
        entries={calEntries.entries}
        projectNames={projectNames}
        designationNames={designationNames}
        projectColors={projectColors}
        isViewingTeammate={isReadOnlyScope}
        readOnlyTooltip={readOnlyTooltip}
        selectedEntryId={selectedEntryId}
        onSelectEntry={handleSelectEntry}
        onEntryEdit={handleEntryEdit}
        onHoverPositionChange={(pos) => {
          hoverPosRef.current = pos;
        }}
        onEmptySlotClick={handleEmptySlotClick}
        onDragMove={handleDragMove}
        onDragResize={handleDragResize}
        nestedScroll={!fullPageScroll}
      />

      <CalendarEntryPopover
        open={popoverOpen}
        anchorPosition={popoverAnchorPos}
        onClose={closePopover}
        startMinute={popoverStart}
        endMinute={popoverEnd}
        date={popoverDate}
        existingEntry={popoverEntry}
        projects={popoverProjects}
        designations={popoverDesignations}
        recentOptions={recentPairOptions}
        taskSuggestions={taskSuggestions}
        onProjectChange={setPopoverProjectId}
        onSave={handleSave}
        onDelete={handleDelete}
      />
    </Box>
  );
}
